import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { API_URL } from '@/config';

type FrontchannelLogout = {
  type: 'frontchannel_logout';
  sid: string;
  redirectTo: string;
  timestamp: number;
  messageId: string;
};

type FrontchannelLogoutAck = {
  type: 'frontchannel_logout_ack';
  messageId: string;
};

const getPostMessageTargetOrigin = (issuer: string | null) => {
  if (!issuer) return null;

  try {
    const issuerOrigin = new URL(issuer).origin;
    const trustedOrigin = new URL(API_URL).origin;

    return issuerOrigin === trustedOrigin ? issuerOrigin : null;
  } catch {
    return null;
  }
};

export const LogoutIframe = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const sid = searchParams.get('sid');
    const action = searchParams.get('action');
    const redirectTo = searchParams.get('redirect_to') || '/auth/login';
    const targetOrigin = getPostMessageTargetOrigin(searchParams.get('iss'));

    if (action !== 'logout' || !sid) return;

    // 一意なID（ACKの突合用）
    const messageId =
      (crypto as Crypto)?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

    const baseMessage: FrontchannelLogout = {
      type: 'frontchannel_logout',
      sid,
      redirectTo,
      timestamp: Date.now(),
      messageId,
    };

    let bc: BroadcastChannel | null = null;
    let resolved = false;
    let attempt = 0;
    let retryTimer: number | null = null;
    const maxAttempts = 5;
    const baseDelayMs = 200; // 200, 400, 800, 1600, 3200ms
    const cleanupFns: Array<() => void> = [];

    const notifyParentLogoutComplete = (success: boolean) => {
      if (!targetOrigin) return;
      if (window.parent === window) return;

      window.parent.postMessage(
        {
          type: 'FRONTCHANNEL_LOGOUT_COMPLETE',
          sessionId: sid,
          timestamp: Date.now(),
          success,
        },
        targetOrigin
      );
    };

    const resolveOnce = ({ notifyParent = true } = {}) => {
      if (resolved) return;
      resolved = true;
      cleanupFns.forEach((fn) => {
        try {
          fn();
        } catch {
          // Ignore cleanup errors
        }
      });
      if (notifyParent) {
        notifyParentLogoutComplete(true);
      }
    };

    // --- ACK 受信（どの経路でもOK）---
    const onWindowMessage = (evt: MessageEvent) => {
      const data = evt.data as FrontchannelLogoutAck | undefined;
      if (
        data &&
        data.type === 'frontchannel_logout_ack' &&
        data.messageId === messageId
      ) {
        resolveOnce();
      }
    };
    window.addEventListener('message', onWindowMessage);
    cleanupFns.push(() =>
      window.removeEventListener('message', onWindowMessage)
    );

    try {
      bc = new BroadcastChannel('frontchannel-logout');
      const onBCMessage = (evt: MessageEvent) => {
        const data = evt.data as FrontchannelLogoutAck | undefined;
        if (
          data &&
          data.type === 'frontchannel_logout_ack' &&
          data.messageId === messageId
        ) {
          resolveOnce();
        }
      };
      bc.addEventListener('message', onBCMessage);
      cleanupFns.push(() => {
        try {
          bc?.removeEventListener('message', onBCMessage);
        } catch {
          // Ignore broadcast channel errors when removing listeners
        }
        try {
          bc?.close();
        } catch {
          // Ignore broadcast channel errors when closing
        }
      });
    } catch {
      // BroadcastChannel 非対応ブラウザは無視
    }

    const onStorage = (e: StorageEvent) => {
      if (
        e.key === 'frontchannel_logout_ack' &&
        typeof e.newValue === 'string'
      ) {
        try {
          const data = JSON.parse(e.newValue) as FrontchannelLogoutAck;
          if (
            data.type === 'frontchannel_logout_ack' &&
            data.messageId === messageId
          ) {
            resolveOnce();
          }
        } catch {
          // Ignore storage errors when parsing
        }
      }
    };
    window.addEventListener('storage', onStorage);
    cleanupFns.push(() => window.removeEventListener('storage', onStorage));

    // --- 送信（3経路へ同報）---
    const sendOnce = () => {
      // 親ウィンドウへ（クロスオリジンなら第二引数は "*" か厳密オリジンに調整）
      try {
        window.parent.postMessage(baseMessage, window.location.origin);
      } catch {
        // Ignore parent window errors when posting messages
      }

      // 同一オリジンの全タブへ
      try {
        bc?.postMessage(baseMessage);
      } catch {
        // Ignore broadcast channel errors when posting messages
      }

      // localStorage フォールバック（他タブへ伝播）
      localStorage.setItem(
        'frontchannel_logout_trigger',
        JSON.stringify({ ...baseMessage, triggerTime: Date.now() })
      );
      // storage イベントは newValue にしか乗らないため短時間でクリア
      setTimeout(() => {
        localStorage.removeItem('frontchannel_logout_trigger');
      }, 100);
    };

    // --- リトライ（指数バックオフ + ジッター）---
    const attemptSend = () => {
      if (resolved || attempt >= maxAttempts) return;
      attempt += 1;
      sendOnce();

      const backoff = baseDelayMs * Math.pow(2, attempt - 1);
      const jitter = Math.floor(Math.random() * 100);
      retryTimer = window.setTimeout(() => {
        if (!resolved) attemptSend();
      }, backoff + jitter);
    };

    attemptSend();

    // セーフガード：上限時間でクリーンアップ（到達保証はしないがハング防止）
    const hardTimeout = window.setTimeout(
      () => resolveOnce({ notifyParent: false }),
      4500
    );
    cleanupFns.push(() => clearTimeout(hardTimeout));
    cleanupFns.push(() => {
      if (retryTimer) clearTimeout(retryTimer);
    });

    // アンマウント時のクリーンアップ
    return () => resolveOnce({ notifyParent: false });
  }, [searchParams]);

  return null;
};
